import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';
import Container from '../../../components/ui/Container';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import TextReveal from '../../../components/ui/TextReveal';
import MagneticButton from '../../../components/ui/MagneticButton';
import ParallaxLayer from '../../../components/ui/ParallaxLayer';

const HeroSection = ({ carouselImages, introComplete }) => {
  const shouldAnimate = introComplete;

  const entrance = (delay = 0) => ({
    initial: { opacity: 0, y: 15, filter: 'blur(4px)' },
    animate: shouldAnimate ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {},
    transition: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
  });

  return (
    <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden">
      {/* Ken Burns Background */}
      <div className="absolute inset-0">
        {carouselImages[0] && (
          <div className="absolute inset-0 animate-ken-burns">
            <OptimizedImage
              src={carouselImages[0].url}
              alt={carouselImages[0].alt}
              className="w-full h-full"
              wrapperClassName="w-full h-full"
              objectFit="cover"
              priority
              aspectRatio="16/9"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-900/90 via-brand-900/40 to-brand-900/95" />
      </div>

      {/* Floating Decorative Elements */}
      <ParallaxLayer speed={0.3} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-[10%] w-32 h-32 border border-brand-400/20 rounded-full animate-spin-slow" />
      </ParallaxLayer>
      <ParallaxLayer speed={0.5} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-[15%] w-20 h-20 bg-gradient-to-br from-brand-400/20 to-brand-600/10 rounded-full blur-xl animate-float" />
      </ParallaxLayer>
      <ParallaxLayer speed={0.2} className="absolute inset-0 pointer-events-none">
        <div
          className="absolute bottom-1/4 left-[20%] w-64 h-64 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #64B2FF 1px, transparent 1px)',
            backgroundSize: '16px 16px',
          }}
        />
      </ParallaxLayer>

      {/* Hero Content */}
      <Container className="relative z-10 text-center py-20 pb-24 sm:pb-20">
        <motion.div {...entrance(0.2)}>
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold text-brand-300 bg-white/10 backdrop-blur-md rounded-pill border border-brand-500/30 text-shimmer">
            🔥 India's Premier Calisthenics Platform
          </span>
        </motion.div>

        <TextReveal
          text="Master Your Body."
          as="h1"
          mode="word"
          stagger={0.08}
          delay={shouldAnimate ? 0.4 : 0}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-2 text-balance"
        />
        <TextReveal
          text="Master Your Life."
          as="p"
          mode="word"
          stagger={0.08}
          delay={shouldAnimate ? 0.8 : 0}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold gradient-text-light mb-6 text-balance"
        />

        <motion.p
          {...entrance(1.0)}
          className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Expert-led calisthenics programs, live coaching, and a thriving community to help you achieve superhuman strength — from anywhere.
        </motion.p>

        <motion.div
          {...entrance(1.2)}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <MagneticButton>
            <Link
              to="/programs"
              className="glow-ring inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 rounded-xl shadow-glow-brand transition-all duration-300 hover:shadow-glow-lg group"
            >
              Explore Programs
              <FiArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </MagneticButton>
          <MagneticButton>
            <Link
              to="/about-us"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 rounded-xl transition-all duration-300"
            >
              Our Story
            </Link>
          </MagneticButton>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          {...entrance(1.5)}
          className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-14"
        >
          {[
            { icon: '💪', text: '2000+ Students' },
            { icon: null, text: '5 Rating', star: true },
            { icon: '🏋️', text: '10+ Programs' },
          ].map((badge, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={shouldAnimate ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 1.6 + i * 0.1, duration: 0.4 }}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-pill text-white/90"
            >
              {badge.star ? (
                <FaStar className="w-4 h-4 text-yellow-400" />
              ) : badge.icon ? (
                <span>{badge.icon}</span>
              ) : null}
              <span className="text-sm font-medium">{badge.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </Container>

      {/* Scroll Indicator — SVG line draw */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={shouldAnimate ? { opacity: 1 } : {}}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-xs text-white/40 uppercase tracking-widest">Scroll</span>
        <svg width="20" height="30" viewBox="0 0 20 30" className="text-white/40">
          <motion.line
            x1="10" y1="0" x2="10" y2="20"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.path
            d="M4 16 L10 22 L16 16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
          />
        </svg>
      </motion.div>
    </section>
  );
};

export default HeroSection;
